/* eslint-disable camelcase */
import {
  Address,
  APIPlace,
  GooglePlaceAddress,
  PlacesApiAddressComponent,
} from "@companion-core/shared/app/Interfaces/adresses";

const formatAddress = (name: string, formattedAddress: string) => {
  return formattedAddress.includes(name) ? formattedAddress : `${name}, ${formattedAddress}`;
};

const getStreet = (address: Pick<Address, "street_number" | "route">): string =>
  [address.street_number, address.route].filter(Boolean).join(" ");

const getName = (
  name: string,
  routeShortText: string,
  address: Pick<Address, "city" | "route" | "country" | "street_number">
): string | null =>
  address?.city?.includes(name) ||
  name.includes(address?.route ?? "") ||
  getStreet(address).includes(name) ||
  name.includes(address?.country) ||
  name.includes(routeShortText)
    ? null
    : name;

function firstComponentLongText(
  components: PlacesApiAddressComponent[],
  ...orderedTypes: string[]
): string | undefined {
  for (const t of orderedTypes) {
    const c = components.find((comp) => comp.types?.includes(t));
    if (c?.longText) return c.longText;
  }
  return undefined;
}

function firstComponentShortText(
  components: PlacesApiAddressComponent[],
  type: string
): string | undefined {
  const c = components.find((comp) => comp.types?.includes(type));
  return c?.shortText;
}

function pickCityFromV2Components(components: PlacesApiAddressComponent[]): string {
  return (
    firstComponentLongText(
      components,
      "locality",
      "postal_town",
      "administrative_area_level_2",
      "administrative_area_level_3",
      "sublocality_level_1",
      "sublocality"
    ) ?? ""
  );
}

function getPlaceLevel(types: string[], expectedTypes: string[]): string {
  for (const type of types) {
    if (expectedTypes.includes(type)) {
      return type;
    }
  }
  return types[0];
}

export function formatLocation(
  data: APIPlace,
  options?: { expectedPlaceLevel?: string }
): GooglePlaceAddress {
  const components = data.addressComponents ?? [];
  const street_number = firstComponentLongText(components, "street_number");
  const route = firstComponentLongText(components, "route");
  const routeShortText = firstComponentShortText(components, "route") ?? "";
  const city = pickCityFromV2Components(components);
  const country = firstComponentLongText(components, "country") ?? "";
  const country_code = (firstComponentShortText(components, "country") ?? "").toLowerCase();
  const state = firstComponentLongText(components, "administrative_area_level_1") ?? "";
  const zipcode = firstComponentLongText(components, "postal_code");
  const type = options?.expectedPlaceLevel
    ? getPlaceLevel(data.types ?? [], options.expectedPlaceLevel.split(","))
    : data.types?.[0];
  const displayName = data.displayName?.text ?? "";
  const formattedRaw = data.formattedAddress ?? "";
  const street = getStreet({ street_number, route });
  const addressLike: Pick<Address, "city" | "route" | "country" | "street_number"> = {
    city,
    route,
    country,
    street_number,
  };

  return {
    formatted_address: formatAddress(displayName, formattedRaw),
    name: getName(displayName, routeShortText, addressLike),
    city,
    country_code,
    latitude: data.location?.latitude ?? null,
    longitude: data.location?.longitude ?? null,
    state_code: state,
    street: street || undefined,
    zip_code: zipcode,
    timezone: data.timeZone?.id ?? "",
    type: type ?? "",
  };
}
